import React from "react";
import dayjs from "dayjs";

/**
 * Ruban d'angle « ANTIDATÉ » affiché sur les documents (CP, proposition, attestation) lorsque le
 * contrat a été antidaté. Coupe en diagonale le coin supérieur droit (touche le bord haut ET le
 * bord droit), sans décaler la mise en page. Le conteneur parent doit être en position relative.
 *
 * Mentionne la DATE D'ÉDITION réelle du document (createdAt) — la date d'effet figure déjà sur le
 * contrat ; le ruban signale donc l'écart entre l'effet (antérieur) et l'édition réelle.
 */
const AntidateBadge = ({ contrat, variant = "ribbon", inset: insetCfg }) => {
  if (!contrat?.antidate) {
    return null;
  }
  // Contrôle : si la date d'antidate (date d'édition réelle = updatedAt, sinon createdAt) tombe
  // le MÊME JOUR que la date d'effet, il n'y a pas d'antidatage réel → ne pas afficher le badge
  // (garde contre un drapeau `antidate` resté à true après historisation/avenant). Égalité LocalDate.
  const antidateDate = contrat?.updatedAt || contrat?.createdAt;
  if (antidateDate && contrat?.dateEffet && dayjs(antidateDate).isSame(dayjs(contrat.dateEffet), "day")) {
    return null;
  }
  const editeLe = contrat?.updatedAt ? dayjs(contrat.updatedAt).format("DD/MM/YYYY") : contrat?.createdAt ? dayjs(contrat.createdAt).format("DD/MM/YYYY") : null;
  const label = `⚠ ANTIDATÉ${editeLe ? ` · édité le ${editeLe}` : ""}`;

  // Variante « inset » : deux rubans d'angle DIAGONAUX qui TRAVERSENT le coin (interceptent les deux
  // bords), en haut-gauche et en bas-droite, mis en retrait des bords pour rester imprimables.
  // PARAMÉTRABLE via la prop `inset` (pour adapter aux différents formats : A4 vs attestation 1080×400).
  // Le conteneur parent doit être en position relative.
  if (variant === "inset") {
    const c = {
      box: 250,        // côté de la boîte de clipping (carré)
      banner: 340,     // largeur de la bannière diagonale
      edge: 12,        // retrait par rapport au bord du document
      pos: 74,         // décalage de la bannière dans la boîte (top/bottom)
      off: 70,         // débordement de la bannière hors de la boîte (left/right)
      fontSize: 11.5,
      line: 18,
      padV: 5,
      ...(insetCfg || {}),
    };
    const banniere = {
      position: "absolute",
      width: c.banner,
      transform: "rotate(-45deg)",
      background: "#cf1322",
      color: "#ffffff",
      textAlign: "center",
      fontWeight: 700,
      fontSize: c.fontSize,
      letterSpacing: 0.4,
      lineHeight: `${c.line}px`,
      padding: `${c.padV}px 0`,
      // Annule un éventuel contour de texte hérité (les attestations imprimées appliquent un
      // -webkit-text-stroke sur tout leur texte) qui ternirait le blanc du badge.
      WebkitTextStroke: "0",
      borderTop: "1px solid rgba(255,255,255,0.55)",
      borderBottom: "1px solid rgba(255,255,255,0.55)",
      // Force le rendu de la couleur de fond à l'impression (sinon certains navigateurs la suppriment).
      WebkitPrintColorAdjust: "exact",
      printColorAdjust: "exact",
    };
    const boite = {
      position: "absolute",
      width: c.box,
      height: c.box,
      overflow: "hidden",
      zIndex: 30,
      pointerEvents: "none",
    };
    return (
      <>
        {/* Haut-gauche */}
        <div style={{ ...boite, top: c.edge, left: c.edge }}>
          <div style={{ ...banniere, top: c.pos, left: -c.off }}>{label}</div>
        </div>
        {/* Bas-droite (symétrique) */}
        <div style={{ ...boite, bottom: c.edge, right: c.edge }}>
          <div style={{ ...banniere, bottom: c.pos, right: -c.off }}>{label}</div>
        </div>
      </>
    );
  }

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        right: 0,
        width: 250,
        height: 250,
        overflow: "hidden",
        zIndex: 30,
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 52,
          right: -76,
          width: 300,
          transform: "rotate(45deg)",
          background: "#cf1322",
          color: "#ffffff",
          textAlign: "center",
          fontWeight: 700,
          fontSize: 11.5,
          letterSpacing: 0.4,
          lineHeight: "18px",
          padding: "5px 0",
          boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
          borderTop: "1px solid rgba(255,255,255,0.45)",
          borderBottom: "1px solid rgba(255,255,255,0.45)",
        }}
      >
        ⚠ ANTIDATÉ{editeLe ? ` · édité le ${editeLe}` : ""}
      </div>
    </div>
  );
};

export default AntidateBadge;
